<template>
  <UIDrawer
    :is-open
    :z-index
    title="Chats"
    size="sm"
    @close="handleClose"
  >
    <div class="ai-agent-conversations">
      <UIButton
        :disabled
        label="New chat"
        variant="secondary"
        fill="outlined"
        size="xs"
        :leading="PencilSquareIcon"
        @click="handleClickNewChat"
      />
      <UIPreloader v-if="loading" message="Loading chats..." />
      <div v-else class="ai-agent-conversations__list">
        <template v-for="[date, items] in conversations" :key="date">
          <div class="ai-agent-conversations__date">{{ date }}</div>
          <UIButton
            v-for="conversation in items"
            :key="conversation.thread_id"
            :disabled
            :variant="aiAgentStore.getCurrentThreadId === conversation.thread_id ? 'secondary' : 'gray'"
            :fill="aiAgentStore.getCurrentThreadId === conversation.thread_id ? 'filled' : 'outlined'"
            size="xs"
            @click="handleClickConversation(conversation.thread_id)"
          >
            <AIAgentConversationsItem
              :conversation="conversation"
              @click:delete="handleClickDelete"
            />
          </UIButton>
        </template>
      </div>
    </div>
  </UIDrawer>
</template>

<script setup lang="ts">
import { computed, onMounted } from 'vue'

import { ModalClass } from '@types'

import { useAIAgentStore } from '@/store/ai/agent'

import { PencilSquareIcon } from '@heroicons/vue/24/outline'

import { UIButton } from '@ui/buttons'

import AIAgentConversationsItem from './AIAgentConversationsItem.vue'
import { UIPreloader } from '@ui/core'
import { UIDrawer } from '@ui/modals'

type Props = {
  modal: ModalClass<any>
}

const props = defineProps<Props>()

type Emits = {
  'new-chat': []
}

const emit = defineEmits<Emits>()

const aiAgentStore = useAIAgentStore()

const isOpen = computed(() => props.modal.isOpened)
const zIndex = computed(() => props.modal.zIndex)

const loading = computed(() => !aiAgentStore.initFlag)
const disabled = computed(() => aiAgentStore.loading || loading.value)
const conversations = computed(() => Object.entries(aiAgentStore.getConversations))

const handleClose = () => {
  props.modal.close()
}

const handleClickNewChat = () => {
  emit('new-chat')
}

const handleClickDelete = (threadId: string) => {
  aiAgentStore.destroy(threadId)
}

const handleClickConversation = (threadId: string) => {
  handleClose()
  if (threadId === aiAgentStore.getCurrentThreadId) return
  aiAgentStore.fetch(threadId)
  aiAgentStore.setCurrentThreadId(threadId)
}

onMounted(() => {
  if (aiAgentStore.initFlag) return
  aiAgentStore.fetchConversations()
})
</script>

<style>
.ai-agent-conversations {
  @apply flex flex-col items-start flex-auto;
  @apply gap-4;

  &__date {
    @apply mt-2;
    @apply text-sm sm:text-xs text-gray-400;
    @apply font-medium tracking-wider;
    @apply uppercase;
  }

  &__list {
    @apply w-full;
    @apply flex flex-col gap-2;

    .ui-button {
      @apply justify-start;
    }
  }

  .ui-button {
    @apply px-3;
    @apply rounded-full;
  }
}
</style>
